import React, { type Dispatch, type SetStateAction, useState } from 'react'
import { uniq } from 'lodash'

import { type FormLabelProps } from '../../ComponentTypes.models'
import Button from '../../Button/Button'
import Icon from '../../Icons/Icon'
import styles from './tag-input.module.scss'
import FormLabel from '../../FormLabel/FormLabel'
import { toast } from '../../Toast/Toast'
import { c } from '../../../translations/LibraryTranslationService'
import TrimText from '../../TrimText/TrimText'

export enum Separators {
  COMMA = ',',
  ENTER = 'Enter',
  TAB = 'Tab',
  SPACE = ' ',
}

const keyCodeMap: { [key: string]: string } = {
  Enter: '\n',
  ',': ',',
  Tab: '\t',
  ' ': ' ',
}

const splitString = (str: string, separators: string[]): string[] => {
  const separatorChars = separators.map((separator) => keyCodeMap[separator])
  const escapedSeparators = separatorChars.map((separator) =>
    separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'),
  )
  const regexPattern = new RegExp(escapedSeparators.join('|'), 'g')
  return str.split(regexPattern).map((tag) => tag.trim())
}

export type TagInputProps = {
  /** Array of tags currently in the input */
  tags: string[]
  /** Function to update the tags array */
  setTags: Dispatch<SetStateAction<string[]>>
  /** Optional array of separators to define how tags are split */
  separators?: Separators[]
  /** Optional limit on the number of tags that can be added */
  tagLimit?: number
  /** Optional props for the form label associated with the input */
  formLabelProps?: FormLabelProps
  /** Optional placeholder text displayed when the input is empty */
  placeholder?: string
  /** Optional boolean to automatically focus the input on render */
  autoFocus?: boolean
  /** Optional name attribute for the input element */
  name?: string
  /** Optional id attribute for the input element */
  id?: string
  /** Optional max height for the tag input (in px) */
  maxHeight?: number
  /** Optional prop to add a test id to the TagInput for QA testing */
  qaTestId?: string
}

const TagInput = ({
  tags,
  setTags,
  separators = [Separators.COMMA, Separators.ENTER],
  tagLimit,
  formLabelProps,
  placeholder,
  autoFocus,
  name,
  id,
  maxHeight = 100,
  qaTestId = 'tag-input',
}: TagInputProps): React.JSX.Element => {
  // Editing state variables
  const [editingTagValue, setEditingTagValue] = useState<string | null>(null)
  const [editingTagText, setEditingTagText] = useState<string>('')

  if (
    separators.some(
      (separator) => !Object.values(Separators).includes(separator),
    )
  ) {
    throw new Error(c('invalidSeparator'))
  }

  const [text, setText] = useState(''),
    inputRef = React.useRef<HTMLInputElement>(null)

  const addTags = (tags: string[]) => {
      setTags((prev) => [...prev, ...tags])
    },
    removeTag = (tag: string) => {
      setTags((prev) => prev.filter((t) => t !== tag))
      if (editingTagValue === tag) {
        exitEditingMode()
      }
    },
    removeTagAtIndex = (index: number) => {
      const tagToRemove = tags[index]
      setTags((prevTags) => ((t) => (t.splice(index, 1), t))([...prevTags]))
      if (editingTagValue === tagToRemove) {
        exitEditingMode()
      }
    }

  const isDuplicateTag = (tagToCheck: string, excludeTag?: string): boolean => {
    const trimmedTag = tagToCheck.trim()
    const tagList = excludeTag ? tags.filter((tag) => tag !== excludeTag) : tags
    return tagList.includes(trimmedTag)
  }

  const showDuplicateTagError = () => {
    toast({
      type: 'error',
      message: c('tagAlreadyExists'),
    })
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (separators.includes(e.key as Separators)) {
      e.preventDefault()

      if (text.trim() === '') return // Don't add empty tags

      if (isDuplicateTag(text)) {
        showDuplicateTagError()
        setText('')
        return
      }
      // Reset editing state
      setEditingTagValue(null)
      setEditingTagText('')

      addTags([text.trim()])
      setText('')
    }

    if (e.key === 'Backspace' && text === '') {
      removeTag(tags[tags.length - 1])
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()

    const paste = e.clipboardData.getData('text')
    const splitTags = splitString(paste, separators)
    const filteredTags = uniq(splitTags).filter(
      (tag) => tag.trim() !== '' && !tags.includes(tag.trim()),
    )

    const allowedTags = tagLimit ? tagLimit - tags.length : filteredTags.length
    const limitedTags = tagLimit
      ? filteredTags.slice(0, allowedTags)
      : filteredTags

    addTags(limitedTags)
  }

  // Editing functions

  const handleTagClick = (tag: string) => {
    setEditingTagValue(tag)
    setEditingTagText(tag)
  }

  const handleEditComplete = (onBlur: boolean) => {
    // Added onBlur to fix the issue where showDuplicateTagError was being called twice simultaneously for onBlur and keyDown events in editing mode
    if (editingTagValue === null) return

    const index = tags.findIndex((t) => t === editingTagValue)
    if (index === -1) {
      exitEditingMode()
      return
    }

    const newTag = editingTagText.trim()

    if (newTag === tags[index]) {
      exitEditingMode()
      return
    }

    if (newTag === '') {
      removeTagAtIndex(index)
      exitEditingMode()
      return
    }

    if (isDuplicateTag(newTag, editingTagValue)) {
      !onBlur && showDuplicateTagError()
      return
    }

    setTags((prevTags) =>
      ((t) => (t.splice(index, 1, newTag), t))([...prevTags]),
    )
    exitEditingMode()
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleEditComplete(false)
    } else if (e.key === 'Escape') {
      exitEditingMode()
    }
  }

  const exitEditingMode = (shouldFocusInput = true) => {
    setEditingTagValue(null)
    setEditingTagText('')
    if (shouldFocusInput) {
      setTimeout(() => {
        inputRef.current?.focus()
      }, 0)
    }
  }

  // Function to handle changes in the editing input
  const handleEditingTagChange = (value: string) => {
    setEditingTagText(value)
  }

  // Handle blur event on the tag input field
  const handleInputBlur = () => {
    const trimmedText = text.trim()
    if (trimmedText !== '') {
      if (isDuplicateTag(trimmedText)) {
        showDuplicateTagError()
      } else {
        addTags([trimmedText])
      }
      setText('')
    }
  }

  return (
    <div data-testid='tag-input-component' qa-test-id={qaTestId}>
      {formLabelProps ? <FormLabel {...formLabelProps} /> : null}
      <div
        className={styles.container}
        style={{ maxHeight }}
        onClick={() => {
          inputRef.current?.focus()
        }}
        role='button'
        data-testid='tag-input-container'
      >
        {tags.map((tag) =>
          editingTagValue === tag ? (
            <input
              key={`editing-${tag}`}
              className={styles.editingTagInput}
              type='text'
              value={editingTagText}
              autoFocus
              onBlur={() => handleEditComplete(true)}
              onChange={(e) => handleEditingTagChange(e.target.value)}
              onKeyDown={handleEditKeyDown}
              onClick={(e) => e.stopPropagation()} // Prevent event from bubbling up
              data-testid='tag-input-editing'
            />
          ) : (
            <div
              key={tag}
              role='button'
              className={styles.tag}
              onClick={(e) => {
                e.stopPropagation() // Stop the event from bubbling up
                handleTagClick(tag)
              }}
              data-testid='tag-input-tag'
            >
              <TrimText text={tag} limit={30} customClass={styles.trimText} />
              <Button
                as='unstyled'
                onMouseDown={(e: React.MouseEvent<HTMLButtonElement>) =>
                  e.preventDefault()
                } // Prevents the input from losing focus
                onClick={() => removeTag(tag)}
              >
                <Icon icon='x' iconSize='8px' className={styles.closeIcon} />
              </Button>
            </div>
          ),
        )}

        <input
          className={styles.tagInput}
          type='text'
          placeholder={placeholder}
          autoFocus={autoFocus}
          name={name}
          id={id}
          value={text}
          ref={inputRef}
          disabled={!!tagLimit && tags.length === tagLimit}
          onKeyDown={handleKeyDown}
          onChange={(e) => setText(e.target.value)}
          onPaste={handlePaste}
          onFocus={() => {
            if (editingTagValue !== null) {
              exitEditingMode(false) // Prevent focusing the input again
            }
          }}
          onBlur={handleInputBlur}
          data-testid='tag-input-field'
        />
        {tags.length > 0 ? (
          <Button
            type='button'
            styleType='text-blue'
            className={styles.clearButton}
            onClick={() => setTags([])}
          >
            {c('clear')}
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export default TagInput
